import { useEffect, useState, useRef } from "react";
import L from "leaflet";
import * as turf from "@turf/turf";
import { useMap } from "react-leaflet";
import { createRoot } from "react-dom/client";
import { FaRuler, FaDrawPolygon, FaTrash, FaTimes, FaList, FaMapMarkerAlt, FaFilePdf } from "react-icons/fa";
import "leaflet-simple-map-screenshoter";
import { jsPDF } from "jspdf";
import { Ruler, Square } from "lucide-react";
import { Pentagon } from "lucide-react";
import { Route } from "lucide-react";
import "./Mapview.css";

export default function MeasureControl() {
  const map = useMap();
  map.createPane("measurementPane");
  map.getPane("measurementPane").style.zIndex = 650;
  const [mode, setMode] = useState(null);
  const [measurementsList, setMeasurementsList] = useState([]);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const controlRef = useRef(null); 
  const measureLayerRef = useRef(null);
  const previewRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);



  

useEffect(() => {
  if (!map) return;

  const container = map.getContainer();

  const intercept = (e) => {
    if (!mode) return;

    const interactiveEl = e.target.closest(".leaflet-interactive");

    if (interactiveEl) {
      // 🔥 Empêche le popup
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

      // 🔥 Re-déclenche un clic propre sur la map
      const point = map.mouseEventToContainerPoint(e);
      const latlng = map.containerPointToLatLng(point);

      map.fire("click", {
        latlng,
        containerPoint: point,
        originalEvent: e,
      });
    }
  };

  if (mode) {
    container.addEventListener("click", intercept, true);
  }

  return () => {
    container.removeEventListener("click", intercept, true);
  };
}, [map, mode]);
useEffect(() => {
  if (!map) return;

  const container = map.getContainer();

  if (mode) {
    container.classList.add("measure-cursor");
  } else {
    container.classList.remove("measure-cursor");
  }
}, [map, mode]);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!map) return;

    const screenshoter = L.simpleMapScreenshoter({
      hideElementsWithSelectors: [
        ".leaflet-control",
        "#measure-panel",
        "#import-Control"
      ],
      screenName: "map_capture",
      exportOnly: true,
      hideElements: true,
      hidden:true,
    }).addTo(map);

    map.screenshoter = screenshoter;
  }, [map]);

  /* ================= PREVIEW ICON (CARRÉ) ================= */
  const previewIcon = L.divIcon({
    className: "",
    html: `
      <div style="
        width:18px;
        height:18px;
        border:2px solid #1a73e8;
        background:rgba(26,115,232,.15);
        border-radius:4px;
        box-shadow:0 0 6px rgba(26,115,232,.6);
      "></div>
    `,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

  const addMeasurementToList = (type, value, unit, points) => {
    const id = Date.now() + Math.random();
    const newMeasurement = {
      id,
      type,
      value: parseFloat(value.toFixed(2)),
      formattedValue: value.toFixed(2),
      unit,
      points,
      color: type === "distance" ? "#b10c0c" : "#066594",
      date: new Date().toLocaleTimeString(),
      typeName: type === "distance" ? "Distance" : "Surface"
    };
    
    setMeasurementsList(prev => [newMeasurement, ...prev]);
    return id;
  };

  const removeMeasurement = (id) => {
    if (!window.measurements) return;
    
    const store = window.measurements;
    const measurementIndex = measurementsList.findIndex(m => m.id === id);
    if (measurementIndex === -1) return;
    

    store.layers.forEach((layer, index) => {
      if (layer._measureId === id) {
        if (measureLayerRef.current && layer) {
          measureLayerRef.current.removeLayer(layer);
        }
        store.layers.splice(index, 1);
      }
    });
    
    setMeasurementsList(prev => prev.filter(m => m.id !== id));
  };

  const cancelCurrentMeasurement = () => {
    if (!window.measurements) return;
    
    const store = window.measurements;
 
    if (store.activeLayer && measureLayerRef.current) {
      measureLayerRef.current.removeLayer(store.activeLayer);
      store.layers = store.layers.filter(l => l !== store.activeLayer);
      store.activeLayer = null;
    }
    
    store.markers.forEach(m => {
    if (m.main) store.markersLayer.removeLayer(m.main);
    if (m.halo) store.markersLayer.removeLayer(m.halo);
  });
  store.markers = [];
    

    store.finished = false;
    setMode(null);
    

    if (controlRef.current && controlRef.current.getContainer()) {
      const container = controlRef.current.getContainer();
      const distanceBtn = container.children[0];
      const surfaceBtn = container.children[1];
      
      if (distanceBtn) distanceBtn.style.background = "white";
      if (surfaceBtn) surfaceBtn.style.background = "white";
    }
  };

  const clearAllMeasurements = () => {
    if (!window.measurements) return;
    
    if (measureLayerRef.current) {
      measureLayerRef.current.clearLayers();
    }
    
    window.measurements.layers = [];
    if (window.measurements.markersLayer) {
      window.measurements.markersLayer.clearLayers();
    }
    window.measurements.activeLayer = null;
    window.measurements.finished = false;
    window.measurements.markers = [];
    setMeasurementsList([]);
  };

  const zoomToMeasurement = (points) => {
    if (!map || !points || points.length === 0) return;
    
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], map.getZoom());
    } else {
      const latlngs = points.map(p => [p.lat, p.lng]);
      const bounds = L.latLngBounds(latlngs);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  const startMeasurement = (measurementType) => {
    if (!window.measurements) return;
    
    window.measurements.finished = false;
    window.measurements.activeLayer = null;
    
    const newMode = mode === measurementType ? null : measurementType;
    setMode(newMode);
    

    if (newMode && !isMobile) {
      setSidebarVisible(true);
    }

    if (controlRef.current && controlRef.current.getContainer()) {
      const container = controlRef.current.getContainer();
      const distanceBtn = container.children[0];
      const surfaceBtn = container.children[1];
      
      if (newMode === "distance") {
        if (distanceBtn) distanceBtn.style.background = "#f0f8ff";
        if (surfaceBtn) surfaceBtn.style.background = "white";
      } else if (newMode === "surface") {
        if (surfaceBtn) surfaceBtn.style.background = "#f0f8ff";
        if (distanceBtn) distanceBtn.style.background = "white";
      } else {
        if (distanceBtn) distanceBtn.style.background = "white";
        if (surfaceBtn) surfaceBtn.style.background = "white";
      }
    }
  };

  const finishMeasurement = () => {
    setMode(null);
    
    if (controlRef.current && controlRef.current.getContainer()) {
      const container = controlRef.current.getContainer();
      const distanceBtn = container.children[0];
      const surfaceBtn = container.children[1];
      
      if (distanceBtn) distanceBtn.style.background = "white";
      if (surfaceBtn) surfaceBtn.style.background = "white";
    }
  };

  const exportToPDF = async () => {
    if (isExporting || !map.screenshoter) return;
    setIsExporting(true);

    try {
      const blob = await map.screenshoter.takeScreen("image/png");
      const imgData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const img = new Image();
      img.src = imgData;

      img.onload = () => {
        const imgWidth = pageWidth - 2 * margin;
        const imgHeight = (img.height * imgWidth) / img.width;

        pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, Math.min(imgHeight, pageHeight * 0.6));

        let yPos = margin + Math.min(imgHeight, pageHeight * 0.6) + 20;
        
        pdf.setFontSize(18);
        pdf.text("RAPPORT DE MESURES", pageWidth / 2, yPos, { align: "center" });
        yPos += 15;

        pdf.setFontSize(10);
        const now = new Date();
        const dateStr = `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()} ${now.getHours()}:${now.getMinutes()}`;
        pdf.text(`Date : ${dateStr}`, margin, yPos);
        yPos += 10;
        
        pdf.setFontSize(12);
        pdf.text(`Numéro de Titre : RAPPORT-${now.getTime().toString().slice(-6)}`, margin, yPos);
        yPos += 15;
        
        const totalDistance = measurementsList
          .filter(m => m.type === "distance")
          .reduce((sum, m) => sum + m.value, 0);
        
        const totalSurface = measurementsList
          .filter(m => m.type === "surface")
          .reduce((sum, m) => sum + m.value, 0);
        
        pdf.setFontSize(14);
        pdf.text("SYNTHÈSE DES MESURES", pageWidth / 2, yPos, { align: "center" });
        yPos += 10;
        
        pdf.setFontSize(11);
        pdf.text(`Nombre total de mesures : ${measurementsList.length}`, margin, yPos);
        yPos += 7;
        pdf.text(`Distance totale : ${totalDistance.toFixed(2)} m`, margin, yPos);
        yPos += 7;
        pdf.text(`Surface totale : ${totalSurface.toFixed(2)} m²`, margin, yPos);
        yPos += 15;
        
        if (measurementsList.length > 0) {
          pdf.setFontSize(14);
          pdf.text("DÉTAIL DES MESURES", pageWidth / 2, yPos, { align: "center" });
          yPos += 15;
          
          pdf.setFontSize(10);

          pdf.setFillColor(240, 240, 240);
          pdf.rect(margin, yPos, pageWidth - 2 * margin, 8, 'F');
          pdf.text("N°", margin + 5, yPos + 5);
          pdf.text("Type", margin + 20, yPos + 5);
          pdf.text("Valeur", margin + 60, yPos + 5);
          pdf.text("Unité", margin + 100, yPos + 5);
          pdf.text("Date/Heure", margin + 120, yPos + 5);
          
          yPos += 8;
          
          measurementsList.forEach((measurement, index) => {
            if (yPos > pageHeight - 20) {
              pdf.addPage();
              yPos = margin;
            }
            
            pdf.text(`${index + 1}`, margin + 5, yPos + 5);
            pdf.text(measurement.typeName, margin + 20, yPos + 5);
            pdf.text(measurement.formattedValue, margin + 60, yPos + 5);
            pdf.text(measurement.unit, margin + 100, yPos + 5);
            pdf.text(measurement.date, margin + 120, yPos + 5);
            
            yPos += 8;
          });
        }

        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(8);
          pdf.text(
            `Page ${i} / ${totalPages}`,
            pageWidth - margin - 20,
            pageHeight - 10
          );
        }
        
        pdf.save(`Rapport-Mesures-${now.getTime()}.pdf`);
        setIsExporting(false);
      };

    } catch (error) {
      console.error("Erreur export PDF:", error);
      alert("Impossible de générer le PDF.");
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (!map) return;

    if (!measureLayerRef.current) {
      measureLayerRef.current = L.layerGroup().addTo(map);
    }

    if (!window.measurements) {
      window.measurements = {
        markersLayer: null,
        layers: [],
        activeLayer: null,
        markers: [],
        finished: false,
        measureLayer: measureLayerRef.current,
      };
    } else {
      window.measurements.measureLayer = measureLayerRef.current;
    }

    if (!window.measurements.markersLayer) {
      window.measurements.markersLayer = L.layerGroup().addTo(map);
    }

    if (!controlRef.current) {
      const container = L.DomUtil.create(
      "div",
      "leaflet-control leaflet-bar custom-top-control"
    );

      const commonStyles = `
        background: rgba(255, 255, 255, 0.95);
        padding: 8px;
        border-radius: 10px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: 10px;
        
      `;

      container.style.cssText = `
      ${commonStyles}
    `;

      const btnStyle = `
        cursor: pointer;
        background: white;
        border-radius: 8px;
        width: 34px;
        height: 34px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 5px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        transition: all 0.2s ease;
      `;

      const btnDistance = L.DomUtil.create("div", "", container);
      btnDistance.style.cssText = btnStyle;
      btnDistance.title = "Mesurer distance";
      createRoot(btnDistance).render(<Route size={18} color="#2980b9" strokeWidth={3}/>);
      btnDistance.onmouseover = () => {
        btnDistance.style.transform = "scale(1.1)";
        btnDistance.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
      };
      btnDistance.onmouseout = () => {
        btnDistance.style.transform = "scale(1)";
        btnDistance.style.boxShadow = "0 2px 6px rgba(0,0,0,0.2)";
      };
      L.DomEvent.on(btnDistance, "click", (e) => {
        L.DomEvent.stopPropagation(e);
        startMeasurement("distance");
      });

      const btnSurface = L.DomUtil.create("div", "", container);
      btnSurface.style.cssText = btnStyle;
      btnSurface.title = "Mesurer surface";
      createRoot(btnSurface).render(<Pentagon size={18} color="#2980b9" strokeWidth={3}  />);
      btnSurface.onmouseover = () => {
        btnSurface.style.transform = "scale(1.1)";
        btnSurface.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
      };
      btnSurface.onmouseout = () => {
        btnSurface.style.transform = "scale(1)";
        btnSurface.style.boxShadow = "0 2px 6px rgba(0,0,0,0.2)";
      };
      L.DomEvent.on(btnSurface, "click", (e) => {
        L.DomEvent.stopPropagation(e);
        startMeasurement("surface");
      });

      const btnClear = L.DomUtil.create("div", "", container);
      btnClear.style.cssText = btnStyle;
      btnClear.title = "Effacer toutes les mesures";
      createRoot(btnClear).render(<FaTrash color="#2980b9" size={16} />);
      btnClear.onmouseover = () => {
        btnClear.style.transform = "scale(1.1)";
        btnClear.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
      };
      btnClear.onmouseout = () => {
        btnClear.style.transform = "scale(1)";
        btnClear.style.boxShadow = "0 2px 6px rgba(0,0,0,0.2)";
      };
      L.DomEvent.on(btnClear, "click", (e) => {
        L.DomEvent.stopPropagation(e);
        clearAllMeasurements();
        setMode(null);
        
        const distanceBtn = container.children[0];
        const surfaceBtn = container.children[1];
        
        if (distanceBtn) distanceBtn.style.background = "white";
        if (surfaceBtn) surfaceBtn.style.background = "white";
      });

      const ControlClass = L.Control.extend({
        options: { position: "topright" },
        onAdd: () => container,
      });
      controlRef.current = new ControlClass();
      map.addControl(controlRef.current);
    }





    return () => {
      if (controlRef.current) {
        map.removeControl(controlRef.current);
        controlRef.current = null;
      }
      if (measureLayerRef.current) {
        map.removeLayer(measureLayerRef.current);
        measureLayerRef.current = null;
      }
    };
  }, [map]);


  useEffect(() => {
  const handleKeyDown = (e) => {
    if (e.key !== "Escape" || isMobile) return;
    if (!window.measurements) return;

    const store = window.measurements;

    // ==============================
    // 1️⃣ SI DESSIN EN COURS → SUPPRESSION TOTALE
    // ==============================
    if (store.activeLayer) {

  // Supprimer couche principale
  if (measureLayerRef.current) {
    measureLayerRef.current.removeLayer(store.activeLayer);
  }

  // 🔥 Supprimer l'ombre
  if (store.shadowLayer) {
    measureLayerRef.current.removeLayer(store.shadowLayer);
    store.shadowLayer = null;
  }

  store.layers = store.layers.filter(l => l !== store.activeLayer);

  store.activeLayer = null;

  // Supprimer points
  store.markers.forEach(marker => {
    store.markersLayer.removeLayer(marker);
  });

  store.markers = [];
  store.finished = false;
  setMode(null);

  return;
}

    // ==============================
    // 2️⃣ SI PAS DE DESSIN → SUPPRIMER DERNIÈRE MESURE
    // ==============================
    if (measurementsList.length > 0) {
      const lastMeasurement = measurementsList[0];
      removeMeasurement(lastMeasurement.id);
    }
  };

  document.addEventListener("keydown", handleKeyDown);

  return () => {
    document.removeEventListener("keydown", handleKeyDown);
  };
}, [measurementsList, isMobile]);

  

  useEffect(() => {
  if (!map || !mode) return;

  const store = window.measurements;

  let tempLine = null;
  let shadowLayer = null;

  /* ================= PREVIEW ================= */

function handleSnapOnFirstPoint(mouseLatLng) {
  if (mode !== "surface") return;
  if (!store.activeLayer) return;
  if (store.markers.length < 3) return;

  const firstMarker = store.markers[0];
  if (!firstMarker) return;

  const firstLatLng = firstMarker.getLatLng();
  const distance = map.distance(mouseLatLng, firstLatLng);

  // 🔥 Effet visuel propre
  firstMarker.setStyle({
    radius: distance < 15 ? 14 : 8,
    fillColor: distance < 15 ? "#ffffff" : "#ffffff",
    color: "#fbbc04"
  });
}

  const onMove = (e) => {
  if (!store.activeLayer || !e.latlng) return;

  handleSnapOnFirstPoint(e.latlng);
  handleSnapOnLastPoint(e.latlng);

  const latlngs = store.activeLayer.getLatLngs();
  const pts = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
  if (!pts.length) return;

  const last = pts[pts.length - 1];

  if (!tempLine) {
    tempLine = L.polyline([last, e.latlng], {
      color: "#fbbc04",
      weight: 3,
      dashArray: "6,6",
      interactive: false
    }).addTo(measureLayerRef.current);
  } else {
    tempLine.setLatLngs([last, e.latlng]);
  }
};

  /* ================= CLICK ================= */

  const handleClick = (e) => {
    if (!mode || store.finished || !e.latlng) return;

    let latlng = e.latlng;
    // ================= SNAP FIN DISTANCE =================
if (mode === "distance" && store.activeLayer) {
  const latlngs = store.activeLayer.getLatLngs();

  if (latlngs.length >= 2) {
    const lastPoint = latlngs[latlngs.length - 1];
    const distance = map.distance(latlng, lastPoint);

    if (distance < 15) {

      const lastMarker = store.markers[store.markers.length - 1];

      if (lastMarker) {
        lastMarker.setStyle({ radius: 14 });

        setTimeout(() => {
          lastMarker.setStyle({ radius: 8 });
        }, 200);
      }

      handleDoubleClick();
      return; // IMPORTANT
    }
  }
}

// ================= SNAP SUR POINT DE DÉPART =================

if (mode === "surface" && store.activeLayer) {
  const latlngs = store.activeLayer.getLatLngs();
  const pts = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;

  if (pts.length >= 3) {
    const firstPoint = pts[0];
    const distance = map.distance(latlng, firstPoint);

if (distance < 15) {

  const firstMarker = store.markers[0];

  if (firstMarker) {
    firstMarker.setStyle({ radius: 14 });

    setTimeout(() => {
      firstMarker.setStyle({ radius: 8 });
    }, 200);
  }

  // 🔥 AJOUTER le premier point pour fermer
  store.activeLayer.addLatLng(firstPoint);

  // 🔥 Fermer immédiatement
  handleDoubleClick();

  return; // ⛔ IMPORTANT
}
  }
}

    if (!store.activeLayer) {
      const layerId = Date.now() + Math.random();

      if (mode === "distance") {

        // LÉGER LISERÉ SOMBRE (FIN)
        shadowLayer = L.polyline([], {
          color: "rgba(0,0,0,0.25)",
          weight: 6,
          interactive: false
        }).addTo(measureLayerRef.current);
        store.shadowLayer = shadowLayer;

        // LIGNE JAUNE
        store.activeLayer = L.polyline([], {
          color: "#fbbc04",
          weight: 3,
          lineCap: "round",
          lineJoin: "round",
          interactive: false
        }).addTo(measureLayerRef.current);

      } else {

        shadowLayer = L.polygon([], {
          color: "rgba(0,0,0,0.25)",
          weight: 6,
          fillOpacity: 0,
          interactive: false
        }).addTo(measureLayerRef.current);
        store.shadowLayer = shadowLayer;

        store.activeLayer = L.polygon([], {
          color: "#fbbc04",
          weight: 3,
          fillColor: "#fbbc04",
          fillOpacity: 0.15,
          interactive: false
        }).addTo(measureLayerRef.current);
      }

      store.activeLayer._measureId = layerId;
      store.layers.push(store.activeLayer);
    }

    store.activeLayer.addLatLng(latlng);

    if (shadowLayer) {
      shadowLayer.setLatLngs(store.activeLayer.getLatLngs());
    }

// Réduire anciens points
store.markers.forEach(m => {
  m.setStyle({ radius: 6 });
});

// Créer point
const marker = L.circleMarker(latlng, {
  pane: "measurementPane",
  radius: 8,
  fillColor: "#ffffff",
  color: "#fbbc04",
  weight: 3,
  fillOpacity: 1,
  opacity: 1,
  interactive: true
}).addTo(store.markersLayer);

store.markers.push(marker);
  };

function createMapLabel(latlng, text, measureId) {
  const label = L.marker(latlng, {
    pane: "measurementPane",
    interactive: false,
    icon: L.divIcon({
      className: "",
      html: `
        <div style="
          font-weight: bold;
          font-size: 14px;
          color: #ffffff;
          text-shadow: 0 0 3px rgba(0,0,0,0.7);
          white-space: nowrap;
        ">
          ${text}
        </div>
      `,
    })
  });

  label._measureId = measureId;

  measureLayerRef.current.addLayer(label);
  window.measurements.layers.push(label);
}
/* ================= DISTANCE ================= */
function handleSnapOnLastPoint(mouseLatLng) {
  if (mode !== "distance") return;
  if (!store.activeLayer) return;

  const latlngs = store.activeLayer.getLatLngs();
  if (!latlngs.length) return;

  const lastPoint = latlngs[latlngs.length - 1];
  const distance = map.distance(mouseLatLng, lastPoint);

  const lastMarker = store.markers[store.markers.length - 1];
  if (!lastMarker) return;

  // Effet visuel
  lastMarker.setStyle({
    radius: distance < 15 ? 14 : 8
  });
}
  /* ================= DOUBLE CLICK ================= */

  const handleDoubleClick = () => {
    if (!store.activeLayer || store.finished) return;

    store.finished = true;

    const latlngs = store.activeLayer.getLatLngs();
    const pts = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;

    if (mode === "distance" && pts.length < 2) return;
    if (mode === "surface" && pts.length < 3) return;

    let value = 0;
    let labelPos = pts[pts.length - 1];

    if (mode === "distance") {

  const coords = pts.map(p => [p.lng, p.lat]);
  const line = turf.lineString(coords);

  // Calcul distance
  value = turf.length(line, { units: "kilometers" }) * 1000;

  // 🔥 Calcul vrai centre géométrique de la ligne
  const totalLength = turf.length(line, { units: "kilometers" });

  const midpointFeature = turf.along(
    line,
    totalLength / 2,
    { units: "kilometers" }
  );

  labelPos = L.latLng(
    midpointFeature.geometry.coordinates[1],
    midpointFeature.geometry.coordinates[0]
  );

  const measureId = addMeasurementToList("distance", value, "m", pts);

  createMapLabel(
    labelPos,
    value.toFixed(2) + " m",
    measureId
  );
} else {
      const coords = [...pts, pts[0]].map(p => [p.lng, p.lat]);
      const poly = turf.polygon([coords]);
      value = turf.area(poly);

      const center = turf.center(poly);
      labelPos = L.latLng(
        center.geometry.coordinates[1],
        center.geometry.coordinates[0]
      );

      const measureId = addMeasurementToList("surface", value, "m²", pts);

      createMapLabel(
        labelPos,
        value.toFixed(2) + " m²",
        measureId
      );
    }

    if (tempLine) {
      measureLayerRef.current.removeLayer(tempLine);
      tempLine = null;
    }

    store.activeLayer = null;

    finishMeasurement();
  };

  map.on("mousemove", onMove);
  if (L.Browser.mobile) {
  map.on("touchstart", handleClick);
} else {
  map.on("click", handleClick);
}
  map.on("dblclick", handleDoubleClick);

  map.getContainer().style.cursor = "crosshair";

  return () => {
    map.off("mousemove", onMove);
    map.off("click", handleClick);
    map.off("dblclick", handleDoubleClick);
    map.getContainer().style.cursor = "";

    if (tempLine) measureLayerRef.current.removeLayer(tempLine);
  };
}, [map, mode]);

  return (
    <>
      {!isMobile && sidebarVisible && (
        <div
          id="measure-panel"
          style={{
            position: "fixed",
            top: isMobile ? "auto" : "65px",
            bottom: isMobile ? "0" : "auto",
            right: isMobile ? "0" : "70px",
            left: isMobile ? "0" : "auto",
            width: isMobile ? "100%" : "370px",
            height: isMobile ? "60vh" : "calc(100vh - 200px)",
            background: "white",
            borderRadius: isMobile ? "16px 16px 0 0" : "12px",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.25)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            fontFamily: "Roboto, Arial, sans-serif",
            animation: isMobile ? "slideUp 0.3s ease" : "none"
          }}
        >
          
          <div style={{
            padding: "14px 18px",
            borderBottom: "1px solid #ddd",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#fff",
            fontSize: "16px",
            fontWeight: "500"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {mode === "distance" ? (
              <Route size={18} color="#2980b9" strokeWidth={3} />
            ) : (
              <Pentagon size={18} color="#2980b9" strokeWidth={3} />
            )}
            {mode === "distance" ? "Distance" : "Polygone"}
          </div>
            <button
              onClick={() => setSidebarVisible(false)}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: isMobile ? "24px" : "20px",
                color: "#444",
                padding: "5px"
              }}
            >
              <FaTimes />
            </button>
          </div>

          <div style={{
            padding: "15px",
            fontSize: "13px",
            color: "#333",
            borderBottom: "1px solid #eee"
          }}>
            {mode
              ? "Cliquez sur la carte pour tracer un chemin ou dessiner un polygone"
              : "Cliquez sur la carte pour tracer un chemin ou dessiner un polygone"}
          </div>

          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: isMobile ? "12px" : "15px",
              WebkitOverflowScrolling: "touch"
            }}
          >
            {measurementsList.length === 0 ? (
              <div style={{ color: "#777", fontSize: "14px", textAlign: "center", paddingTop: "30px" }}>
                Aucune mesure encore
              </div>
            ) : (
              measurementsList.map(meas => (
                <div key={meas.id} style={{
                  padding: "8px 12px",
                  marginBottom: "10px",
                  borderBottom: "1px solid #ddd",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div>
                    <div style={{ fontWeight: "bold", fontSize: "14px", color: meas.color }}>
                      {meas.type === "distance" ? "Distance" : "Surface"}
                    </div>
                    <div style={{ fontSize: "18px", fontWeight: "500" }}>
                      {meas.formattedValue} {meas.unit}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {measurementsList.length > 0 && (
            <div style={{
              padding: "15px",
              borderTop: "1px solid #ddd",
              display: "flex",
              flexDirection: "column",
              gap: "10px"
            }}>
              <button
                onClick={exportToPDF}
                disabled={isExporting}
                style={{
                  width: "100%",
                  background: isExporting ? "#95a5a6" : "#2ecc71",
                  color: "white",
                  padding: "12px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  border: "none",
                  cursor: isExporting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  fontWeight: "bold"
                }}
              >
                <FaFilePdf />
                {isExporting ? "Export en cours..." : "Exporter en PDF"}
              </button>
              
              <button 
                onClick={() => { 
                  if (window.confirm("Voulez-vous vraiment effacer toutes les mesures ?")) { 
                    clearAllMeasurements(); 
                  } 
                }} 
                style={{ 
                  width: "100%", 
                  background: "#d93025", 
                  color: "white", 
                  padding: "12px", 
                  borderRadius: "8px", 
                  fontSize: "14px", 
                  border: "none", 
                  cursor: "pointer" 
                }} 
              >
                Effacer tout
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}